import {MatDialog, MatDialogRef, MatDialogState} from '@angular/material/dialog';
import {ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, take} from 'rxjs';
import {ScrollRestorationService} from '@aviary/services/scroll-restoration.service';
import {
  LOADING_INDICATOR_SPINNER_DIALOG_CONFIG,
  LoadingIndicatorSpinnerDialog,
  LoadingIndicatorSpinnerDialogData
} from '@aviary/components/loading-indicator-spinner/loading-indicator-spinner-dialog/loading-indicator-spinner.dialog';

@Component({
  selector: 'mx-loading-indicator-spinner',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadingIndicatorSpinnerComponent implements OnInit, OnDestroy {
  @Input() public delayLoad = true;

  private readonly matDialog: MatDialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly scrollRestorationService = inject(ScrollRestorationService);
  private navigationId: number | undefined = this.router.currentNavigation()?.id ?? this.router.lastSuccessfulNavigation?.id;

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(event => {
      // The global loading indicator can remain mounted while a new route or Hawk's automatic search resubmission loads.
      this.navigationId = event.id;
    });
  }

  public ngOnInit(): void {
    this.dialogRef = this.matDialog.open<LoadingIndicatorSpinnerDialog, LoadingIndicatorSpinnerDialogData, void>(
      LoadingIndicatorSpinnerDialog,
      {
        ...LOADING_INDICATOR_SPINNER_DIALOG_CONFIG,
        data: {
          delayLoad: this.delayLoad
        }
      }
    );
  }

  public ngOnDestroy(): void {
    const navigationId: number | undefined = this.navigationId;

    if (this.dialogRef.getState() === MatDialogState.CLOSED) {
      // If the dialog has already been closed, we don't need to wait for afterClosed to restore scroll position.
      this.scrollRestorationService.restoreScrollWhenLoaded(navigationId);
    } else {
      // The dialog is still open, so we need to wait for afterClosed to restore scroll position.
      // eslint-disable-next-line rxjs-angular/prefer-takeuntil -- We only need to do this once.
      this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
        this.scrollRestorationService.restoreScrollWhenLoaded(navigationId);
      });
      this.dialogRef.close();
    }
  }

  private dialogRef: MatDialogRef<LoadingIndicatorSpinnerDialog>;
}
